import { useState } from 'react'

import { Dialog, Field, Text, TextArea } from '@loadsmart/miranda-react'

import { validateComment } from 'assistant/domain/feedbackComment'

type FeedbackCommentDialogProps = {
  onSave: (comment: string) => Promise<void>
  onClose: () => void
}

/** Asked after a thumbs down. The rating is already saved; the comment is optional. */
export default function FeedbackCommentDialog({ onSave, onClose }: FeedbackCommentDialogProps) {
  const [value, setValue] = useState('')
  const [message, setMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  async function send() {
    const result = validateComment(value)

    if (!result.ok) {
      setMessage(result.reason)
      return
    }

    // Nothing to add: the rating is saved already.
    if (!result.comment) {
      onClose()
      return
    }

    setMessage('')
    setIsSaving(true)

    try {
      await onSave(result.comment)
      onClose()
    } catch {
      setMessage("Couldn't save your comment. Try again.")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open size="small" variant="neutral" onClose={onClose}>
      <Dialog.Header>
        What went wrong?
        <Dialog.Close />
      </Dialog.Header>
      <Dialog.Body>
        <Text variant="body-md" color="color-text-secondary">
          You can leave this empty.
        </Text>
        <Field>
          <TextArea
            aria-label="What went wrong?"
            rows={4}
            value={value}
            onInput={event => {
              setValue((event.target as unknown as HTMLTextAreaElement).value)
              setMessage('')
            }}
          />
        </Field>
        {message && (
          <Text variant="body-sm" color="color-danger-60" role="alert">
            {message}
          </Text>
        )}
      </Dialog.Body>
      <Dialog.Footer />
      <Dialog.Actions>
        <Dialog.ActionTertiary onClick={onClose}>Skip</Dialog.ActionTertiary>
        <Dialog.ActionPrimary onClick={send} loading={isSaving}>
          Send
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )
}
